/**
 * BackendErrorScreen — User-friendly error or provisioning status screen when backend is connecting/unavailable.
 */

import { clearTokens } from "@/lib/tokenStore";

interface BackendErrorScreenProps {
  title?: string;
  message?: string;
  icon?: React.ReactNode;
  isProvisioning?: boolean;
  bootStage?: string;
  bootProgress?: number;
  eta?: number;
  onRetry?: () => void;
  onSwitchLicense?: () => void;
}

export function BackendErrorScreen({
  title = "Connection Lost",
  message = "The service isn't available right now. Please try again in a moment.",
  icon = (
    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="var(--status-warn)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto" }}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  isProvisioning = false,
  bootStage = "allocating_gpu",
  bootProgress = 10,
  eta = 0,
  onRetry = () => window.location.reload(),
  onSwitchLicense,
}: BackendErrorScreenProps) {
  const handleSwitchLicense = () => {
    if (onSwitchLicense) {
      onSwitchLicense();
    } else {
      clearTokens();
      window.location.href = "/activate";
    }
  };

  // Determine stage status
  // Stage 1: Cloud GPU Allocation
  const isStage1Done = bootProgress > 10 || bootStage !== "allocating_gpu";
  const isStage1Active = bootStage === "allocating_gpu";

  // Stage 2: Environment & Model Initialization
  const isStage2Active = ["code_sync", "verifying_environment", "loading_models"].includes(bootStage) || (bootProgress >= 20 && bootProgress < 95);
  const isStage2Done = bootProgress >= 95 || bootStage === "starting_agent";

  // Stage 3: LiveKit WebRTC Streaming
  const isStage3Active = bootStage === "starting_agent" || bootProgress >= 95;
  const isStage3Done = bootProgress >= 100;

  // Detail text for stage 2
  let stage2Detail = "Warming up CUDA 12.8 & neural models...";
  if (bootStage === "code_sync") {
    stage2Detail = "Syncing StreamPersona workspace...";
  } else if (bootStage === "verifying_environment") {
    stage2Detail = "Verifying PyTorch CUDA 12.8 Blackwell engine...";
  } else if (bootStage === "loading_models") {
    stage2Detail = "Loading neural face swap & voice cloning models...";
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: "var(--bg-base)",
        padding: "var(--space-4)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          textAlign: "center",
          padding: "var(--space-6) var(--space-8)",
          background: "var(--bg-overlay)",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border-subtle)",
          boxShadow: isProvisioning ? "0 8px 32px rgba(0, 0, 0, 0.4)" : undefined,
        }}
      >
        {/* Icon */}
        <div
          style={{
            fontSize: 48,
            marginBottom: "var(--space-3)",
            opacity: isProvisioning ? 0.95 : 0.7,
            animation: isProvisioning ? "pulse 2s infinite ease-in-out" : undefined,
          }}
        >
          {icon}
        </div>

        {/* Title */}
        <h1
          style={{
            fontSize: "var(--text-lg)",
            fontWeight: "var(--weight-semibold)",
            color: "var(--text-primary)",
            marginBottom: "var(--space-2)",
          }}
        >
          {title}
        </h1>

        {/* Provisioning Progress & Countdown */}
        {isProvisioning ? (
          <div style={{ marginBottom: "var(--space-5)" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "var(--text-xs)",
                color: "var(--text-secondary)",
                marginBottom: "var(--space-2)",
              }}
            >
              <span style={{ color: "var(--accent)", fontWeight: "var(--weight-medium)" }}>
                {eta > 0 ? `Ready in ~${eta}s` : "Finalizing connection..."}
              </span>
              <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-muted)" }}>
                {bootProgress}%
              </span>
            </div>

            {/* Progress Bar */}
            <div
              style={{
                width: "100%",
                height: 6,
                background: "var(--bg-elevated)",
                borderRadius: "var(--radius-full)",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, Math.max(5, bootProgress))}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, var(--accent) 0%, #38bdf8 100%)",
                  borderRadius: "var(--radius-full)",
                  transition: "width 0.4s ease-out",
                  boxShadow: "0 0 12px var(--accent-glow)",
                }}
              />
            </div>

            {/* 3-Stage Progress Card */}
            <div
              style={{
                marginTop: "var(--space-4)",
                background: "var(--bg-raised)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-3)",
                textAlign: "left",
                display: "flex",
                flexDirection: "column",
                gap: "var(--space-3)",
              }}
            >
              {/* Stage 1: Allocation */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-3)" }}>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    background: isStage1Done
                      ? "var(--status-live-dim)"
                      : isStage1Active
                      ? "var(--accent-dim)"
                      : "var(--bg-elevated)",
                    color: isStage1Done
                      ? "var(--status-live)"
                      : isStage1Active
                      ? "var(--accent)"
                      : "var(--text-muted)",
                    fontWeight: "bold",
                    flexShrink: 0,
                  }}
                >
                  {isStage1Done ? "✓" : isStage1Active ? "●" : "1"}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--weight-medium)",
                      color: isStage1Done || isStage1Active ? "var(--text-primary)" : "var(--text-muted)",
                    }}
                  >
                    Cloud GPU Allocation
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                    {isStage1Done ? "NVIDIA RTX 5090 Blackwell node reserved" : "Securing dedicated 5090 instance..."}
                  </div>
                </div>
              </div>

              {/* Stage 2: Initialization */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-3)" }}>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    background: isStage2Done
                      ? "var(--status-live-dim)"
                      : isStage2Active
                      ? "var(--accent-dim)"
                      : "var(--bg-elevated)",
                    color: isStage2Done
                      ? "var(--status-live)"
                      : isStage2Active
                      ? "var(--accent)"
                      : "var(--text-muted)",
                    fontWeight: "bold",
                    flexShrink: 0,
                  }}
                >
                  {isStage2Done ? "✓" : isStage2Active ? "●" : "2"}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--weight-medium)",
                      color: isStage2Done || isStage2Active ? "var(--text-primary)" : "var(--text-muted)",
                    }}
                  >
                    Environment & Neural Models
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                    {isStage2Done ? "CUDA 12.8 & neural weights ready" : isStage2Active ? stage2Detail : "Pre-baked Blackwell nightly container"}
                  </div>
                </div>
              </div>

              {/* Stage 3: LiveKit Streaming */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-3)" }}>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    background: isStage3Done
                      ? "var(--status-live-dim)"
                      : isStage3Active
                      ? "var(--accent-dim)"
                      : "var(--bg-elevated)",
                    color: isStage3Done
                      ? "var(--status-live)"
                      : isStage3Active
                      ? "var(--accent)"
                      : "var(--text-muted)",
                    fontWeight: "bold",
                    flexShrink: 0,
                  }}
                >
                  {isStage3Done ? "✓" : isStage3Active ? "●" : "3"}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--weight-medium)",
                      color: isStage3Done || isStage3Active ? "var(--text-primary)" : "var(--text-muted)",
                    }}
                  >
                    WebRTC Streaming Tunnel
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                    {isStage3Done ? "Direct peer connection established" : isStage3Active ? "Connecting to LiveKit room..." : "Encrypted sub-30ms loopback transit"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Standard Error Message */
          <p
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--text-secondary)",
              lineHeight: "var(--leading-relaxed)",
              marginBottom: "var(--space-6)",
            }}
          >
            {message}
          </p>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <button
            onClick={onRetry}
            style={{
              width: "100%",
              padding: "var(--space-3) var(--space-4)",
              background: "var(--accent)",
              color: "#000",
              border: "none",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--weight-semibold)",
              cursor: "pointer",
              fontFamily: "var(--font-sans)",
              transition: "background var(--duration-fast)",
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.background = "var(--accent-hover)";
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.background = "var(--accent)";
            }}
          >
            {isProvisioning ? "Refresh Status" : "Retry Connection"}
          </button>

          <button
            onClick={handleSwitchLicense}
            style={{
              width: "100%",
              padding: "var(--space-2) var(--space-4)",
              background: "transparent",
              color: "var(--text-muted)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--text-xs)",
              cursor: "pointer",
              fontFamily: "var(--font-sans)",
            }}
          >
            Enter Different License Key
          </button>
        </div>
      </div>
    </div>
  );
}
